import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, X, Loader2, Sparkles } from "lucide-react";
import { LandingStaticSections } from "@/components/landing/LandingStaticSections";
import { LandingNavigation } from "@/components/landing/LandingNavigation";
import { HeroSearchDropdown } from "@/components/landing/HeroSearchDropdown";
import { NearbyWatersSection } from "@/components/landing/NearbyWatersSection";

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const heroImages = [
  { url: "https://images.unsplash.com/photo-1493787039806-2edcbe808750?w=1920&q=85", alt: "Angler fly fishing on a river with mountain Alps in the background" },
  { url: "https://images.unsplash.com/photo-1545450660-3378a7f3a364?w=1920&q=85", alt: "Man fly fishing during daytime in natural setting" },
  { url: "https://images.unsplash.com/photo-1599161954112-300ca11ca7da?w=1920&q=85", alt: "Green pine trees near river under blue sky" },
  { url: "https://images.unsplash.com/photo-1498855926480-d98e83099315?w=1920&q=85", alt: "Sunrise in valley with water stream" },
  { url: "https://images.unsplash.com/photo-1565242047446-e0fa93b0961e?w=1920&q=85", alt: "Sunset over a pristine fishing river" }
];

export default function LandingPage() {
  const navigate = useNavigate();

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [nextImageIndex, setNextImageIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const [nearbyDestinations, setNearbyDestinations] = useState([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [userState, setUserState] = useState(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setNextImageIndex((currentImageIndex + 1) % heroImages.length);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
        setIsTransitioning(false);
      }, 1000);
    }, 6000);
    return () => clearInterval(interval);
  }, [currentImageIndex]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ latitude, longitude });
          fetchNearbyDestinations(latitude, longitude);
          try {
            const response = await axios.get(`${API}/geocode/reverse`, { params: { lat: latitude, lon: longitude } });
            if (response.data?.state) setUserState(response.data.state);
          } catch (error) {}
        },
        () => fetchFeaturedDestinations(),
        { timeout: 10000, maximumAge: 300000 }
      );
    } else {
      fetchFeaturedDestinations();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNearbyDestinations = async (lat, lng, retryCount = 0) => {
    if (retryCount === 0) setNearbyLoading(true);
    try {
      const response = await axios.get(`${API}/destinations/nearby`, { params: { latitude: lat, longitude: lng, limit: 8, radius: 200 }, timeout: 15000 });
      setNearbyDestinations(response.data || []);
      setNearbyLoading(false);
    } catch (error) {
      if (retryCount < 2) setTimeout(() => fetchNearbyDestinations(lat, lng, retryCount + 1), Math.pow(2, retryCount) * 500);
      else fetchFeaturedDestinations();
    }
  };

  const fetchFeaturedDestinations = async (retryCount = 0) => {
    if (retryCount === 0) setNearbyLoading(true);
    try {
      const response = await axios.get(`${API}/destinations`, { params: { limit: 8, featured: true }, timeout: 15000 });
      setNearbyDestinations(response.data?.destinations || response.data || []);
      setNearbyLoading(false);
    } catch (error) {
      if (retryCount < 2) setTimeout(() => fetchFeaturedDestinations(retryCount + 1), Math.pow(2, retryCount) * 500);
      else setNearbyLoading(false);
    }
  };

  const fetchResults = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) { setResults(null); return; }
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/search`, { params: { q: searchQuery, limit: 8, include_geocode: true } });
      setResults(response.data);
      setIsOpen(true);
      setSelectedIndex(-1);
    } catch (error) { setResults(null); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchResults(debouncedQuery); }, [debouncedQuery, fetchResults]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && inputRef.current && !inputRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNearbyWaters = () => {
    const params = new URLSearchParams();
    if (userLocation) { params.set('lat', userLocation.latitude.toString()); params.set('lng', userLocation.longitude.toString()); params.set('zoom', '10'); params.set('view', 'map'); }
    if (userState) params.set('state', userState);
    navigate(`/destinations${params.toString() ? '?' + params.toString() : ''}`);
  };

  const handleSelectLocation = (location) => {
    const params = new URLSearchParams();
    params.set('lat', location.latitude);
    params.set('lng', location.longitude);
    params.set('zoom', location.type === "location" ? '10' : '12');
    if (location.type === "destination") params.set('destId', location.data.id);
    else if (location.type === "access_point") params.set('apId', location.data.id);
    params.set('view', 'map');
    navigate(`/destinations?${params.toString()}`);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || !results) return;
    const allResults = [
      ...(results.destinations || []).map(d => ({ ...d, type: 'destination' })),
      ...(results.access_points || []).map(a => ({ ...a, type: 'access_point' })),
      ...(results.locations || []).map(l => ({ ...l, type: 'location' }))
    ];
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(prev => Math.max(prev - 1, -1)); }
    else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      const selected = allResults[selectedIndex];
      if (selected) handleSelectLocation({ latitude: selected.latitude, longitude: selected.longitude, type: selected.type, data: selected });
    }
    else if (e.key === 'Escape') setIsOpen(false);
  };

  const clearSearch = () => { setQuery(""); setResults(null); setIsOpen(false); inputRef.current?.focus(); };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background" data-testid="landing-page">
      <LandingNavigation navigate={navigate} handleNearbyWaters={handleNearbyWaters} />

      {/* Hero Section */}
      <section className="relative h-[70vh] sm:h-screen flex items-start sm:items-center justify-center overflow-hidden pt-20 sm:pt-0">
        <div className="absolute inset-0 z-0">
          <img src={heroImages[currentImageIndex].url} alt={heroImages[currentImageIndex].alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000" style={{ opacity: isTransitioning ? 0 : 1 }} />
          <img src={heroImages[nextImageIndex].url} alt={heroImages[nextImageIndex].alt} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000" style={{ opacity: isTransitioning ? 1 : 0 }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 w-full max-w-3xl mx-auto mt-8 sm:mt-0">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white mb-6 sm:mb-8 leading-tight">Find your water</h1>
          <div className="relative max-w-xl mx-auto mb-6">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input ref={inputRef} type="text" placeholder="Search rivers, lakes, or locations..." value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={handleKeyDown} onFocus={() => results && setIsOpen(true)} className="w-full h-14 pl-12 pr-12 text-lg bg-white border-0 rounded-full shadow-2xl focus-visible:ring-2 focus-visible:ring-accent" data-testid="hero-search-input" />
              {isLoading ? (
                <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground animate-spin" />
              ) : query && (
                <button onClick={clearSearch} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-muted rounded-full transition-colors"><X className="w-4 h-4 text-muted-foreground" /></button>
              )}
            </div>
            {isOpen && <HeroSearchDropdown ref={dropdownRef} results={results} selectedIndex={selectedIndex} handleSelectLocation={handleSelectLocation} query={query} isLoading={isLoading} />}
          </div>
          <Button onClick={() => navigate('/ai-guide')} className="bg-accent hover:bg-accent/90 text-accent-foreground h-12 px-8 rounded-full text-base font-medium shadow-lg" data-testid="ai-guide-hero-btn">
            <Sparkles className="w-4 h-4 mr-2" />Plan a Trip
          </Button>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {heroImages.map((img, idx) => (
            <button key={`hero-${img.alt}`} onClick={() => { setNextImageIndex(idx); setIsTransitioning(true); setTimeout(() => { setCurrentImageIndex(idx); setIsTransitioning(false); }, 1000); }} className={`w-2 h-2 rounded-full transition-all ${idx === currentImageIndex ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/75'}`} aria-label={`Go to image ${idx + 1}`} />
          ))}
        </div>
      </section>

      <NearbyWatersSection userLocation={userLocation} nearbyDestinations={nearbyDestinations} nearbyLoading={nearbyLoading} navigate={navigate} />
      <LandingStaticSections />
    </div>
  );
}
